// SundaySermonCard — shared "Sunday's sermon"/"Latest sermon" card, per
// docs/FIX-THIS-WEEK-SERMON.md ("it's the same card as Sermons, larger, and
// it shares one component with Sermons"). Used by SermonsTab.jsx (compact)
// and ThisWeekTab.jsx's no-devotional state (large).
//
// Props:
//   sermon       — sermon row (with sermon_refs) — id, title, display_title,
//                   preacher, date, youtube_id, duration, playlists, series
//   size         — 'compact' | 'large'
//   eyebrow      — label text, e.g. "Latest sermon" / "Sunday's sermon"
//   plan, follow — the sermon's own published reading plan + the user's follow
//                   row, or null — drives the "5-day devotional" pill
//   onOpenSermon(sermon)
//   onOpenPlanDay(plan, follow, dayNumber)
//   onOpenSeries(seriesKey) — optional; omit to let a tap on the series line
//     fall through to onOpenSermon (This Week has no series-browsing overlay)

import { useEffect, useState } from "react";
import { supabase } from "../supabase";
import { displayTitleOf, firstPassageOf } from "../lib/sermonSeries";

const GOLD = "var(--gold)";
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d + (d.length <= 10 ? "T00:00:00" : "")).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
  catch { return d; }
}

// sermons.duration is a free-text column — sometimes already formatted
// ("38:12"), sometimes a raw-seconds string from an upload path that never
// formatted it (e.g. "1774"). Tolerate both rather than rendering digits.
function fmtDurationBadge(duration) {
  if (!duration) return "";
  const str = String(duration).trim();
  if (str.includes(":")) return str;
  const n = Number(str);
  if (!Number.isFinite(n) || n <= 0) return "";
  const mins = Math.floor(n / 60);
  const secs = Math.floor(n % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function seriesKeyOf(sermon) {
  if (!sermon) return "";
  if (Array.isArray(sermon.playlists) && sermon.playlists.length) {
    return (sermon.playlists.find(p => p && p.trim()) || "").trim();
  }
  return (sermon.series || "").trim();
}

export default function SundaySermonCard({ sermon, size = "compact", eyebrow, plan, follow, onOpenSermon, onOpenPlanDay, onOpenSeries }) {
  // ai_summary is a per-sermon blob deliberately excluded from useSermons.js's
  // shared list query (same reason that hook already excludes `transcript` —
  // it ballooned list payloads to megabytes). One small query per card,
  // tolerating the column not existing yet (mirrors FullSermonSheet).
  const [summary, setSummary] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!sermon?.id) { setSummary(""); return; }
    supabase.from("sermons").select("ai_summary").eq("id", sermon.id).maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error && /ai_summary/i.test(error.message || "")) { setSummary(""); return; }
        setSummary(data?.ai_summary || "");
      });
    return () => { cancelled = true; };
  }, [sermon?.id]);

  if (!sermon) return null;

  const large = size === "large";
  const thumbBase = sermon.youtube_id ? `https://i.ytimg.com/vi/${sermon.youtube_id}` : null;
  const refs = sermon.sermon_refs || [];
  const refCount = refs.length;
  const hasKeyMoments = refs.some(r => r.timestamp_s != null || r.timestamp);
  const seriesKey = seriesKeyOf(sermon);

  // "passage · date · preacher" — displayTitleOf() never falls back to the
  // passage now (sermonSeries.js), so this never risks repeating it.
  const meta = [firstPassageOf(sermon), fmtDate(sermon.date), sermon.preacher].filter(Boolean).join(" · ");

  return (
    <div style={large ? S.cardLarge : S.cardCompact}>
      <div style={large ? S.clickAreaLarge : S.clickAreaCompact} onClick={() => onOpenSermon?.(sermon)}>
        <div style={large ? S.thumbWrapLarge : S.thumbWrapCompact}>
          {thumbBase ? (
            <img src={`${thumbBase}/maxresdefault.jpg`} alt="" style={S.thumb}
              onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = `${thumbBase}/hqdefault.jpg`; }}/>
          ) : <div style={{ ...S.thumb, background: "var(--border)" }}/>}
          <div style={large ? S.playDotLarge : S.playDotCompact}>▶</div>
          {fmtDurationBadge(sermon.duration) && <div style={S.durationBadge}>{fmtDurationBadge(sermon.duration)}</div>}
        </div>
        <div style={large ? S.textColLarge : { flex: 1, minWidth: 0 }}>
          <div style={S.eyebrow}>{eyebrow}</div>
          <div style={large ? S.titleLarge : S.titleCompact}>{displayTitleOf(sermon)}</div>
          <div style={S.meta}>{meta}</div>
          {seriesKey && (
            <div style={S.seriesLine} onClick={onOpenSeries ? (e) => { e.stopPropagation(); onOpenSeries(seriesKey); } : undefined}>
              {seriesKey} series
            </div>
          )}
        </div>
      </div>
      {(plan || summary || refCount > 0 || hasKeyMoments) && (
        <div style={S.pillsRow} onClick={() => onOpenSermon?.(sermon)}>
          {plan && (
            <span style={S.pillDevo} onClick={e => { e.stopPropagation(); onOpenPlanDay?.(plan, follow, follow?.last_opened_day || 1); }}>
              5-day devotional
            </span>
          )}
          {summary && <span style={S.pillSummary}>Summary</span>}
          {refCount > 0 && <span style={S.pillRefs}>{refCount} Bible ref{refCount === 1 ? "" : "s"}</span>}
          {hasKeyMoments && <span style={S.pillMoments}>Key moments</span>}
          <svg aria-hidden="true" style={{ marginLeft: "auto", flexShrink: 0 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold-ink)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6"/>
          </svg>
        </div>
      )}
    </div>
  );
}

const S = {
  cardCompact: { background: "var(--white)", borderRadius: 24, padding: 12, display: "flex", flexDirection: "column", gap: 12 },
  cardLarge: { background: "var(--white)", borderRadius: 28, padding: 12, display: "flex", flexDirection: "column", gap: 12 },
  clickAreaCompact: { display: "flex", gap: 12, alignItems: "center", cursor: "pointer" },
  clickAreaLarge: { display: "flex", flexDirection: "column", gap: 10, cursor: "pointer" },

  thumbWrapCompact: { position: "relative", flexShrink: 0, width: 128, height: 72, borderRadius: 14, overflow: "hidden", background: "var(--border)" },
  thumbWrapLarge: { position: "relative", width: "100%", aspectRatio: "16/9", borderRadius: 18, overflow: "hidden", background: "var(--border)" },
  thumb: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  playDotCompact: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16, textShadow: "0 1px 4px rgba(0,0,0,0.5)" },
  playDotLarge: { position: "absolute", left: "50%", top: "50%", width: 48, height: 48, marginLeft: -24, marginTop: -24, borderRadius: 24, background: "rgba(255,255,255,0.92)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink)", fontSize: 18 },
  durationBadge: { position: "absolute", right: 8, bottom: 8, background: "rgba(31,27,22,0.75)", color: "#fff", fontSize: 11, fontWeight: 700, borderRadius: 6, padding: "3px 7px" },

  textColLarge: { display: "flex", flexDirection: "column" },
  eyebrow: { fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--gold-ink)" },
  titleCompact: { fontSize: 17, fontWeight: 800, color: "var(--ink)", margin: "2px 0" },
  titleLarge: { fontSize: 20, fontWeight: 800, color: "var(--ink)", margin: "4px 0", letterSpacing: "-0.01em" },
  meta: { fontSize: 12.5, color: "var(--stone)" },
  seriesLine: { fontSize: 12, fontWeight: 600, color: "var(--gold-ink)", marginTop: 2, cursor: "pointer" },

  pillsRow: { display: "flex", alignItems: "center", gap: 6, cursor: "pointer", overflow: "hidden" },
  pillDevo: { display: "inline-flex", alignItems: "center", background: GOLD, color: "var(--ink)", borderRadius: 999, padding: "6px 10px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", cursor: "pointer", fontFamily: SANS },
  pillSummary: { display: "inline-flex", alignItems: "center", background: "#F5E4B8", color: "#7A5A12", borderRadius: 999, padding: "6px 10px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", fontFamily: SANS },
  pillRefs: { display: "inline-flex", alignItems: "center", background: "#DCD3F5", color: "#2E2552", borderRadius: 999, padding: "6px 10px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", fontFamily: SANS },
  pillMoments: { display: "inline-flex", alignItems: "center", background: "#CFE0F2", color: "#253A50", borderRadius: 999, padding: "6px 10px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", fontFamily: SANS },
};
